import React from 'react';
import { Link } from 'react-router-dom';

const ALLOWED_PROTOCOLS = ['http:', 'https:', 'mailto:', 'tel:'];

export function isSafeInlineUrl(url: string) {
  if (!url || /[\u0000-\u0020\u007f\\]/.test(url)) return false;
  if (url.startsWith('/') && !url.startsWith('//')) return true;
  if (url.startsWith('#')) return true;
  try {
    const parsed = new URL(url);
    return ALLOWED_PROTOCOLS.includes(parsed.protocol);
  } catch {
    return false;
  }
}

function isInternalUrl(url: string) {
  return url.startsWith('/') && !url.startsWith('//');
}

type Token =
  | { type: 'text'; value: string }
  | { type: 'strong'; value: string }
  | { type: 'em'; value: string }
  | { type: 'link'; label: string; href: string };

const PATTERN = /(\*\*[^*\n]+?\*\*|\*[^*\n]+?\*|\[[^\]\n]+?\]\((?:[^()\n]|\([^()\n]*\))+\))/g;

function tokenize(input: string): Token[] {
  const tokens: Token[] = [];
  let lastIndex = 0;
  for (const match of input.matchAll(PATTERN)) {
    const start = match.index ?? 0;
    if (start > lastIndex) {
      tokens.push({ type: 'text', value: input.slice(lastIndex, start) });
    }
    const matched = match[0];

    if (matched.startsWith('**')) {
      tokens.push({ type: 'strong', value: matched.slice(2, -2) });
    } else if (matched.startsWith('[')) {
      const linkMatch = matched.match(/^\[([^\]]+)\]\((.+)\)$/);
      if (linkMatch) {
        const [, label, href] = linkMatch;
        if (isSafeInlineUrl(href.trim())) {
          tokens.push({ type: 'link', label, href: href.trim() });
        } else {
          tokens.push({ type: 'text', value: label });
        }
      } else {
        tokens.push({ type: 'text', value: matched });
      }
    } else {
      tokens.push({ type: 'em', value: matched.slice(1, -1) });
    }

    lastIndex = start + matched.length;
  }

  if (lastIndex < input.length) {
    tokens.push({ type: 'text', value: input.slice(lastIndex) });
  }

  return tokens;
}

type RenderOptions = {
  linkClassName?: string;
};

export function hasInlineMarkdown(input: string | undefined | null) {
  return Boolean(input && !input.matchAll(PATTERN).next().done);
}

export function renderInlineMarkdown(input: string | undefined | null, options: RenderOptions = {}): React.ReactNode {
  if (!input) return null;
  const tokens = tokenize(input);
  const linkClassName = options.linkClassName ?? 'underline decoration-2 underline-offset-4 hover:opacity-80';

  return (
    <>
      {tokens.map((token, idx) => {
        const key = `${token.type}-${idx}`;
        if (token.type === 'text') return <React.Fragment key={key}>{token.value}</React.Fragment>;
        if (token.type === 'strong') return <strong key={key}>{renderInlineMarkdown(token.value, options)}</strong>;
        if (token.type === 'em') return <em key={key}>{renderInlineMarkdown(token.value, options)}</em>;

        if (isInternalUrl(token.href)) {
          return (
            <Link key={key} to={token.href} className={linkClassName}>
              {renderInlineMarkdown(token.label, options)}
            </Link>
          );
        }

        const isExternal = /^https?:/i.test(token.href);
        return (
          <a
            key={key}
            href={token.href}
            target={isExternal ? '_blank' : undefined}
            rel={isExternal ? 'noopener noreferrer' : undefined}
            className={linkClassName}
          >
            {renderInlineMarkdown(token.label, options)}
          </a>
        );
      })}
    </>
  );
}
